// React
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
// MUI
import { Stack, Divider } from "@mui/material";
// Others
import { useSnackbar } from "notistack";
import EmployeeInfo from "../component/EmployeeInfo";
import EmployeeSettlement from "../component/EmployeeSettlement";
import EmployeeProcessorAccInfo from "../component/EmployeeProcessorAccInfo";

import { updateEmployee, updateEmployeeMidTid } from "../model";
import {
  filterDifferences,
} from "../../../merchants/offline-merchant-detail/utils";
import useFormUndo from "utils/useFormUndo/useFormUndo";
import { formPreventEnterSubmit } from "utils/utils";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";
import { FormBuilder } from "components/FormBuilder/FormBuilder";

const EMPLOYEE_LOCALE_KEY = "page.employees.";

/**
 * @param {Object} props
 * @param {import("utils/usePageWrapper/usePageWrapper").CallApisFn<import("../OfflineEmployeeDetail").offlineEmployeeDetailPartitionKeyType>} props.callApis 
 * @returns 
 */
const EmployeeDetail = ({ callApis, employeeDetail, redirectFn, fetchEmployeeDetail }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, reset } = formProps
  const { rememberForm, undoForm, clearFormMemory } = useFormUndo({ formProps });
  const { PageStickyFooter } = usePageWrapper();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isOtherProcessor, setIsOtherProcessor] = useState(false);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const onSubmit = async (formValue) => {
    try {
      const employeeFields = Object.keys(
        t("page.employees.employeeDetail.detail.fields", {
          returnObjects: true,
        }),
      );
      setIsLoading(true);

      // Because firstname and lastname in DB is one word.
      const payload = filterDifferences(employeeDetail, formValue, [
        ...employeeFields,
        "firstname",
        "lastname",
        "isAutoSettlement",
      ]);
      if (
        Object.keys(payload).includes("settlementDate") &&
        payload.settlementDate !== ""
      ) {
        payload.isAutoSettlement = true;
      } else {
        payload.isAutoSettlement = false;
      }

      console.log("Employee detail update payload", payload);
      if (Object.keys(payload).length === 0) {
        snackbar(
          t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.noChanges"),
          "info",
        );
        return;
      }
      const [updateEmployeeResp] = await callApis([{
        api: updateEmployee,
        payload: {
          employeeId: employeeDetail.employeeId,
          merchantId: employeeDetail.merchantId,
          email: employeeDetail.email,
          ...payload,
        },
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      if (updateEmployeeResp.isSuccess) {
        snackbar(
          t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateSuccess"),
          "success",
        );
        await fetchEmployeeDetail(employeeDetail.employeeId);
        setIsEditing(false);
        clearFormMemory();
      } else {
        updateEmployeeResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed"),
          "error",
        );
      }
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const buttons = () => (
    <Stack direction="row" justifyContent="flex-end" gap={2}>
      {!isEditing ? (
        <ButtonMAS
          variant="primary"
          type="button"
          onClick={() => {
            setIsEditing(true);
            rememberForm();
          }}
        >
          {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.edit`)}
        </ButtonMAS>
      ) : (
        <>
          <ButtonMAS
            variant="bordered"
            onClick={() => {
              setIsEditing(false);
              undoForm();
            }}
            type="button"
            disabled={isLoading}
          >
            {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.cancel`)}
          </ButtonMAS>
          <ButtonMAS
            variant="primary"
            type="submit"
            isLoading={isLoading}
          >
            {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.save`)}
          </ButtonMAS>
        </>
      )}
    </Stack>
  );

  const setAllValues = (initialValue) => {
    reset({
      ...initialValue,
      permission: JSON.parse(initialValue?.permission ?? "{}")
        ?.allowedComponent,
    });
  };

  const onEditRow = async (row) => {
    try {
      const payload = {
        employeeId: employeeDetail.employeeId,
        merchantId: employeeDetail.merchantId,
        mid: row?.mid,
        tid: row?.tid,
        paymentGateway: row?.paymentGateway,
      };
      const [updateEmployeeMidTidResp] = await callApis([{
        api: updateEmployeeMidTid,
        payload: payload,
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      if (updateEmployeeMidTidResp.isSuccess) {
        snackbar(
          t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateSuccess"),
          "success",
        );
        await fetchEmployeeDetail(employeeDetail.employeeId);
        return true;
      } else {
        updateEmployeeMidTidResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed"),
          "error",
        );
      }
      return false;
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
      return false;
    }
  };

  useEffect(() => {
    if (employeeDetail) {
      setAllValues(employeeDetail);
      const preference = JSON.parse(employeeDetail?.preference ?? "{}");
      const isOtherProcessor = Object.values(preference?.gatewayMap ?? {}).some(
        (processor) => ["DBS", "DSB", "GLOBALPAYMENTS"].includes(processor),
      );
      setIsOtherProcessor(isOtherProcessor);
    }
  }, [employeeDetail]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
      <FormBuilder.Section>
        <EmployeeInfo
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
          redirectFn={redirectFn}
        />
        <Divider />
        <EmployeeSettlement
          callApis={callApis}
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
          employee={employeeDetail}
          isOtherProcessor={isOtherProcessor}
        />
        <PageStickyFooter>
          {buttons()}
        </PageStickyFooter>
        <Divider />
        <EmployeeProcessorAccInfo
          employee={employeeDetail}
          isLoading={isLoading}
          onEditRow={onEditRow}
        />
      </FormBuilder.Section>
    </form>
  );
};

export default React.memo(EmployeeDetail);
